import { FC } from "react";
import {
  MessageCircle,
  UserPlus,
  Activity,
  Calendar,
  Power,
  User,
  UserCog,
} from "lucide-react";
import dayjs from "dayjs";
import { SYSTEM_POSITIONS_DISPLAY_MAP } from "../../../utils";
import { mapAgentTopic, mapPipelineName } from "../../../utils/valueMap";
import { cn } from "@/lib/utils";
import AssignmentRationaleDetail from "./AssignmentRationaleDetail";

type Activity = {
  id: string;
  merchantId: string;
  leadId: string;
  type:
    | "conversationStarted"
    | "assignment"
    | "pipelinePositionChanged"
    | "appointment"
    | "takenByEmployee"
    | "agentTurnedOff"
    | "agentTurnedOn"
    | "aiAgentAssignment"
    | "manualReassignment"
    | "partnerCrmReassignment";
  conversationStarted?: {
    startedAt: string;
  };
  assignment?: {
    employeeName: string;
    assignedAt: string;
    rationaleText?: string;
    rationaleTrace?: string;
  };
  pipelinePositionChanged?: {
    pipelinePosition: string;
    trigger: string;
    changedAt: string;
    changedBy?: string;
  };
  appointment?: {
    date: string;
    time: string;
    employeeName: string;
  };
  takenByEmployee?: {
    employeeName: string;
    takenAt: string;
  };
  agentTurnedOff?: {
    reason: string;
    turnedAt: string;
    turnedBy: string;
  };
  agentTurnedOn?: {
    reason: string;
    turnedAt: string;
    turnedBy: string;
  };
  aiAgentAssignment?: {
    topic: string;
    pipelineId?: string;
    changedAt: string;
  };
  manualReassignment?: {
    newEmployeeName: string;
    previousEmployeeName?: string;
    reassignedAt: string;
    reassignedBy?: string;
  };
  partnerCrmReassignment?: {
    employeeName: string;
    previousEmployeeName?: string;
    assignedAt: string;
  };
  createdAt: string;
  updatedAt: string;
};

type ActivityMessageProps = {
  activity: Activity;
  allPositions: Array<{ id: string; displayName: string }>;
};

const colorSchemes: Record<string, { bg: string; border: string; icon: string }> = {
  conversationStarted: { bg: "bg-gray-50", border: "border-gray-200", icon: "text-gray-600" },
  pipelinePositionChanged: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600" },
  agentTurnedOff: { bg: "bg-gray-50", border: "border-gray-200", icon: "text-gray-600" },
  aiAgentAssignment: { bg: "bg-blue-50", border: "border-blue-200", icon: "text-blue-600" },
  manualReassignment: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600" },
  partnerCrmReassignment: { bg: "bg-purple-50", border: "border-purple-200", icon: "text-purple-600" },
  default: { bg: "bg-green-50", border: "border-green-100", icon: "text-green-600" },
};

const iconMap: Record<string, typeof Activity> = {
  conversationStarted: MessageCircle,
  assignment: UserPlus,
  pipelinePositionChanged: Activity,
  appointment: Calendar,
  agentTurnedOff: Power,
  agentTurnedOn: Power,
  takenByEmployee: User,
  aiAgentAssignment: UserPlus,
  manualReassignment: UserCog,
  partnerCrmReassignment: UserCog,
};

const ActivityMessage: FC<ActivityMessageProps> = ({
  activity,
  allPositions,
}) => {
  const colorScheme = colorSchemes[activity.type] || colorSchemes.default;
  const IconComponent = iconMap[activity.type] || Activity;

  const getText = () => {
    switch (activity.type) {
      case "assignment": {
        // The headline is frozen at decision time and names the rule that actually applied
        // to THIS assignment. Activities recorded before rationale shipped simply fall back
        // to the plain label — correct and silent, rather than confidently wrong.
        const frozenHeadline = activity.assignment?.rationaleText?.split("\n\n")[0];
        if (frozenHeadline) return frozenHeadline;

        return `${activity.assignment?.employeeName || ""} asignado`;
      }
      case "pipelinePositionChanged":
        const positionId = activity.pipelinePositionChanged?.pipelinePosition || "";
        const position = allPositions.find((p) => p.id === positionId);
        const positionName =
          position?.displayName || SYSTEM_POSITIONS_DISPLAY_MAP[positionId] || positionId;
        const changedBy = activity.pipelinePositionChanged?.changedBy;
        const statusText = `Estado: Prospecto → ${positionName}`;
        return changedBy ? `${statusText} · por ${changedBy}` : statusText;
      case "agentTurnedOff":
        return "IA desactivada";
      case "agentTurnedOn":
        return "IA activada";
      case "takenByEmployee":
        const employee = activity.takenByEmployee?.employeeName || "";
        return `Conversación tomada por ${employee}`;
      case "appointment":
        const appointmentDate = activity.appointment?.date || "";
        const appointmentTime = activity.appointment?.time || "";
        let formattedDate = "";
        if (appointmentDate) {
          const dateObj = dayjs(appointmentDate, "YYYY-MM-DD");
          const day = dateObj.date();
          const monthNames = [
            "Ene", "Feb", "Mar", "Abr", "May", "Jun",
            "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
          ];
          const month = monthNames[dateObj.month()];
          formattedDate = `${day} ${month}`;
        }
        const timeStr = appointmentTime ? appointmentTime.substring(0, 5) : "";
        return `Cita tentativa creada: ${formattedDate} ${timeStr}`;
      case "conversationStarted":
        return "Conversación iniciada";
      case "aiAgentAssignment": {
        const pipelineId = activity.aiAgentAssignment?.pipelineId;
        const topic = activity.aiAgentAssignment?.topic || "";
        const label =
          (pipelineId && mapPipelineName[pipelineId as keyof typeof mapPipelineName]) ??
          mapAgentTopic[topic as keyof typeof mapAgentTopic] ??
          pipelineId ??
          topic;
        return `Agente IA asignado: ${label}`;
      }
      case "manualReassignment": {
        const newName = activity.manualReassignment?.newEmployeeName ?? "";
        const previousName = activity.manualReassignment?.previousEmployeeName;
        const by = activity.manualReassignment?.reassignedBy;
        const base = previousName
          ? `Reasignación manual: ${previousName} → ${newName}`
          : `Reasignación manual a ${newName}`;
        return by ? `${base} · por ${by}` : base;
      }
      case "partnerCrmReassignment": {
        const employeeName = activity.partnerCrmReassignment?.employeeName || "";
        const previous = activity.partnerCrmReassignment?.previousEmployeeName;
        const suffix = previous ? ` (desde ${previous})` : "";
        return `Reasignado a ${employeeName}${suffix} • CRM externo`;
      }
      default:
        return "Actividad";
    }
  };

  const formatTime = (dateTime: string) => {
    return dayjs(dateTime).format("DD/MM/YYYY HH:mm");
  };

  const getTimestamp = () => {
    switch (activity.type) {
      case "assignment":
        return activity.assignment?.assignedAt || activity.createdAt;
      case "pipelinePositionChanged":
        return activity.pipelinePositionChanged?.changedAt || activity.createdAt;
      case "takenByEmployee":
        return activity.takenByEmployee?.takenAt || activity.createdAt;
      case "agentTurnedOff":
        return activity.agentTurnedOff?.turnedAt || activity.createdAt;
      case "agentTurnedOn":
        return activity.agentTurnedOn?.turnedAt || activity.createdAt;
      case "conversationStarted":
        return activity.conversationStarted?.startedAt || activity.createdAt;
      case "appointment":
        return activity.createdAt;
      case "aiAgentAssignment":
        return activity.aiAgentAssignment?.changedAt || activity.createdAt;
      case "manualReassignment":
        return activity.manualReassignment?.reassignedAt || activity.createdAt;
      case "partnerCrmReassignment":
        return activity.partnerCrmReassignment?.assignedAt || activity.createdAt;
      default:
        return activity.createdAt;
    }
  };

  const rationaleText = activity.assignment?.rationaleText;
  const hasRationaleDetail = !!rationaleText && rationaleText.includes("\n\n");

  return (
    <div className="mb-4 flex items-center justify-center px-4">
      <div className="flex w-full flex-col items-center gap-0">
        <div
          className={cn(
            "w-fit rounded-lg border px-4 py-2",
            colorScheme.bg,
            colorScheme.border,
            // Explanations need room to breathe; plain activity pills stay compact.
            hasRationaleDetail ? "w-full max-w-[95%] md:max-w-[36rem]" : "max-w-[80%]"
          )}
        >
          <div className="flex items-center gap-2">
            <IconComponent className={`size-4 shrink-0 ${colorScheme.icon}`} />
            <p className="text-sm font-medium text-gray-800">{getText()}</p>
          </div>

          {hasRationaleDetail && (
            <AssignmentRationaleDetail
              text={rationaleText}
              className="mt-1 border-t border-gray-200 pt-1"
            />
          )}
        </div>
        <p className="mt-2 text-xs text-gray-500">{formatTime(getTimestamp())}</p>
      </div>
    </div>
  );
};

export default ActivityMessage;
